import { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import type { ThinkingBlock } from '@/hooks/use-streaming-v2';

interface ReasoningPillProps {
  thinking: ThinkingBlock;
  isStreaming: boolean;
}

/**
 * Collapsed-by-default reasoning summary pill (directive §8.1, Phase 2 Session 4).
 *
 * Live state (isStreaming && durationMs === null):
 *   `▸ Reasoning... (2.4s)` — duration ticks every 100ms via local nowTick;
 *   chevron pulses via animate-pulse-status.
 *
 * Static state (durationMs set):
 *   `▸ Reasoned for 2.4s` — chevron static.
 *
 * Empty thinking text after stop (Opus 4.7 default behavior per directive §9.2)
 * renders the explicit `(no thinking content)` fallback in the expanded body.
 *
 * Click toggles the inline expanded view (max-height 320px, scrolls).
 */
export function ReasoningPill({ thinking, isStreaming }: ReasoningPillProps) {
  const [expanded, setExpanded] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  const isLive = isStreaming && thinking.durationMs === null;

  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => setNowTick(Date.now()), 100);
    return () => clearInterval(interval);
  }, [isLive]);

  const elapsedMs = isLive ? nowTick - thinking.startedAt : (thinking.durationMs ?? 0);
  const elapsedSec = Math.floor(elapsedMs / 100) / 10;

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        data-testid="reasoning-pill"
        data-state={isLive ? 'live' : 'static'}
        className={clsx(
          'self-start inline-flex items-center gap-1.5 px-2 py-1 rounded-[2px] border',
          'border-panel-border bg-panel-bg hover:border-panel-border-hi',
          'text-micro font-mono text-text-muted tracking-[0.3px]',
        )}
      >
        <ChevronRight
          className={clsx(
            'w-3 h-3 transition-transform',
            expanded && 'rotate-90',
            isLive && 'animate-pulse-status',
          )}
        />
        {isLive ? (
          <span>Reasoning... ({elapsedSec.toFixed(1)}s)</span>
        ) : (
          <span>Reasoned for {elapsedSec.toFixed(1)}s</span>
        )}
      </button>
      {expanded && (
        <div
          data-testid="reasoning-pill-body"
          className={clsx(
            'ml-3 max-h-[320px] overflow-y-auto px-3 py-2',
            'border border-panel-border bg-panel-bg rounded-[2px]',
            'text-xs font-mono text-text-muted whitespace-pre-wrap',
          )}
        >
          {thinking.text || (isLive ? 'Streaming…' : '(no thinking content)')}
        </div>
      )}
    </div>
  );
}
